import {
  Form, Field, InjectedFormProps, change, formValueSelector,
} from 'redux-form';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  DisputeBillable,
  DisputeDefenseOutcome,
  DisputeProcessor,
  DisputeReportingCategory,
  DisputeStatus,
} from '@flowio/api-internal-constants';
import {
  Card, CardContent,
} from '@flowio/react-card';

import { Grid, Column, Row } from '@flowio/react-grid';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { DisputeCard } from '../order-disputes/order-disputes';
import FormNames from '../../constants/FormNames';

import Description from '../../../../components/description/description';
import DescriptionGroup from '../../../../components/description/description-group';
import useThunkDispatcher from '../../../../hooks/useThunkDispatcher';

import { dropdownLabel } from './edit-dispute-form.styles';
import { RenderDateTimePicker } from '../../../../components/ReduxFormFieldAdapters';

const enumToStrList = <T extends object>(enumObj: T): string[] => Object
  .keys(enumObj)
  .filter((value) => typeof value === 'string');

const toOpt = (opt: string) => <option key={opt} value={opt}>{startCase(opt)}</option>;

const enumToOptions = <T extends object>(enumObj: T): JSX.Element[] => enumToStrList(enumObj)
  .map(toOpt);

export interface EditDisuteFormProps extends io.flow.internal.v0.models.Dispute {
  disputeDetails: io.flow.internal.v0.models.DisputeDetail;
}

const validateDefendedAt = (value: Date, allValues: EditDisuteFormProps) => {
  const issuedAt = allValues?.disputeDetails?.dispute?.issued_at;
  const defenseOutcome:string | undefined = allValues.defense_outcome;

  if (defenseOutcome === 'DEFENDED' && !value) {
    return 'Defended date must be provided when defense outcome is DEFENDED';
  }

  if (value && issuedAt && value < moment(issuedAt).toDate()) {
    return 'Defended date must be after dispute date';
  }
  return undefined;
};

const EditDisuteForm: React.FC<InjectedFormProps<EditDisuteFormProps>> = ({
  handleSubmit,
  initialValues,
}) => {
  const selector = formValueSelector(FormNames.EDIT_DISPUTE_FORM);
  const dispatch = useThunkDispatcher();
  const defenseOutcome = useSelector((state: string) => selector(state, 'defense_outcome'));

  const handleDefenseOutcomeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value !== 'DEFENDED') {
        dispatch(change(FormNames.EDIT_DISPUTE_FORM, 'defended_at', null));
      } else {
        dispatch(change(FormNames.EDIT_DISPUTE_FORM, 'defended_at', initialValues.defended_at));
      }
      return value;
    }, [dispatch, initialValues.defended_at],
  );

  return (
    <>
      {
        initialValues?.disputeDetails && (
          <DisputeCard
            disputeDetail={initialValues.disputeDetails}
            handleUpdate={null}
            isFlowEmployee={false}
          />
        )
      }
      <Card>
        <CardContent>
          <Grid gutter={false}>
            <Form onSubmit={handleSubmit}>
              <Row gutter={false}>
                <Column span={6}>
                  <DescriptionGroup spacing={false} className={''}>
                    <Description>
                      <Description.Term className={dropdownLabel} content="Billable" />
                      <Description.Definition>
                        <Field
                          component="select"
                          name="billable"
                          type="text"
                        >
                          {enumToOptions(DisputeBillable)}
                        </Field>
                      </Description.Definition>
                    </Description>
                    <Description>
                      <Description.Term className={dropdownLabel} content="Defense Outcome" />
                      <Description.Definition>
                        <Field
                          component="select"
                          name="defense_outcome"
                          type="text"
                          onChange={handleDefenseOutcomeChange}
                        >
                          {enumToOptions(DisputeDefenseOutcome)}
                        </Field>
                      </Description.Definition>
                    </Description>
                    <Description>
                      <Description.Term className={dropdownLabel} content="Defended At(UTC)" />
                      <Description.Definition>
                        <Field
                          name="defended_at"
                          component={RenderDateTimePicker}
                          validate={validateDefendedAt}
                          disabled={defenseOutcome !== 'DEFENDED'}
                        />
                      </Description.Definition>
                    </Description>
                  </DescriptionGroup>
                </Column>
                <Column span={6}>
                  <DescriptionGroup spacing={false} className={''}>
                    <Description>
                      <Description.Term className={dropdownLabel} content={'Chargeback Category'} />
                      <Description.Definition>
                        <Field
                          component="select"
                          name="reporting_category"
                          type="text"
                        >
                          {enumToOptions(DisputeReportingCategory)}
                        </Field>
                      </Description.Definition>
                    </Description>
                    <Description>
                      <Description.Term className={dropdownLabel} content="Status" />
                      <Description.Definition>
                        <Field
                          component="select"
                          name="status"
                          type="text"
                        >
                          {
                          (initialValues.disputeDetails?.dispute?.processor
                            === DisputeProcessor.PAYPAL)
                            ? enumToOptions(DisputeStatus)
                            : enumToStrList(DisputeStatus)
                              // REFUNDED status only available for Paypal disputes
                              .filter((e) => (e !== DisputeStatus.REFUNDED.toUpperCase()))
                              .map(toOpt)
                        }
                        </Field>
                      </Description.Definition>
                    </Description>
                  </DescriptionGroup>
                </Column>
              </Row>
            </Form>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default EditDisuteForm;
